import React, { useEffect } from 'react';
import { CreditCard as CreditCardIcon } from 'lucide-react';
import { Card, CardHeader, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { ProgressBar } from '../shared/ProgressBar';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency } from '../../utils/formatters';
import { calculateUtilization } from '../../utils/calculations';
import { CreditCard } from '../../types';

interface CreditCardsWidgetProps {
  onAdd: () => void;
  variant?: 'card' | 'page';
}

export const CreditCardsWidget: React.FC<CreditCardsWidgetProps> = ({ onAdd, variant = 'card' }) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { creditCards, updateCreditCard, handleDeleteCreditCard, handleBatchDelete } = useFinancialData();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('credit-cards');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteCreditCard(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Credit Cards', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, creditCards.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSaveBalance = async (index: number) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateCreditCard(index, 'balance', value, sheetId);
    }
  };

  const handleSaveAvailable = async (index: number) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      const card = creditCards[index];
      const newBalance = Math.max(0, card.limit - value);
      await updateCreditCard(index, 'balance', newBalance, sheetId);
    }
  };

  const handleSaveLimit = async (index: number) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateCreditCard(index, 'limit', value, sheetId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, onSave: () => void) => {
    if (e.key === 'Enter') onSave();
    if (e.key === 'Escape') cancelEdit();
  };

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Credit Cards"
        icon={<CreditCardIcon size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={creditCards.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('credit-cards')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
      {editMode.isEditMode && (
        <EditModeActionBar
          selectedCount={editMode.selectedIndices.size}
          totalCount={creditCards.length}
          allSelected={editMode.selectedIndices.size === creditCards.length}
          onSelectAll={() => editMode.selectAll(creditCards.length)}
          onDeselectAll={editMode.deselectAll}
          onDeleteSelected={editMode.deleteSelected}
          isDeleting={editMode.isDeleting}
        />
      )}

      <div className="space-y-4 text-sm">
        {creditCards.map((card: CreditCard, idx: number) => {
          const utilization = calculateUtilization(card.balance, card.limit);

          return (
            <div key={idx}>
              <div
                className={editMode.isEditMode ? 'flex items-start gap-2' : ''}
              >
                {editMode.isEditMode && (
                  <div className="pt-1 flex-shrink-0">
                    <EditModeCheckbox
                      checked={editMode.selectedIndices.has(idx)}
                      onToggle={() => editMode.toggleIndex(idx)}
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                <div className="flex justify-between mb-1 items-center">
                  <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {card.name}
                  </span>
                  <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {privacyMode ? '••••' : `${utilization.toFixed(0)}%`}
                  </span>
                </div>

                <ProgressBar
                  value={card.balance}
                  max={card.limit}
                  height="sm"
                />

                <div className="flex justify-between mt-1">
                  {isEditing('creditCard', idx, 'balance') ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, () => handleSaveBalance(idx))}
                      onBlur={() => handleSaveBalance(idx)}
                      onFocus={e => e.target.select()}
                      className={`w-16 md:w-20 px-2 py-0.5 text-xs rounded border ${
                        darkMode
                          ? 'bg-slate-800 border-slate-600 text-white'
                          : 'bg-white border-slate-300 text-slate-800'
                      } focus:outline-none focus:ring-1 focus:ring-purple-500 text-right`}
                      autoFocus
                    />
                  ) : (
                    <span
                      onClick={() => card.editable && startEdit('creditCard', idx, 'balance', card.balance)}
                      className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'} ${
                        card.editable ? 'cursor-pointer hover:bg-white/10 rounded px-1' : ''
                      }`}
                    >
                      {formatCurrency(card.balance, privacyMode)}
                    </span>
                  )}

                  {isEditing('creditCard', idx, 'limit') ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, () => handleSaveLimit(idx))}
                      onBlur={() => handleSaveLimit(idx)}
                      onFocus={e => e.target.select()}
                      className={`w-16 md:w-20 px-2 py-0.5 text-xs rounded border ${
                        darkMode
                          ? 'bg-slate-800 border-slate-600 text-white'
                          : 'bg-white border-slate-300 text-slate-800'
                      } focus:outline-none focus:ring-1 focus:ring-purple-500 text-right`}
                      autoFocus
                    />
                  ) : (
                    <span
                      onClick={() => card.editable && startEdit('creditCard', idx, 'limit', card.limit)}
                      className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'} ${
                        card.editable ? 'cursor-pointer hover:bg-white/10 rounded px-1' : ''
                      } text-right`}
                    >
                      {formatCurrency(card.limit, privacyMode)}
                    </span>
                  )}
                </div>

                {/* Available Credit row */}
                <div className="flex justify-between mt-0.5">
                  <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Available</span>
                  {isEditing('creditCard', idx, 'available') ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, () => handleSaveAvailable(idx))}
                      onBlur={() => handleSaveAvailable(idx)}
                      onFocus={e => e.target.select()}
                      className={`w-16 md:w-20 px-2 py-0.5 text-xs rounded border ${
                        darkMode
                          ? 'bg-slate-800 border-slate-600 text-white'
                          : 'bg-white border-slate-300 text-slate-800'
                      } focus:outline-none focus:ring-1 focus:ring-purple-500 text-right`}
                      autoFocus
                    />
                  ) : (
                    <span
                      onClick={() => card.editable && startEdit('creditCard', idx, 'available', Math.max(0, card.limit - card.balance))}
                      className={`text-xs ${darkMode ? 'text-green-400/70' : 'text-green-600/70'} ${
                        card.editable ? 'cursor-pointer hover:bg-white/10 rounded px-1' : ''
                      } text-right`}
                    >
                      {formatCurrency(Math.max(0, card.limit - card.balance), privacyMode)}
                    </span>
                  )}
                </div>
                </div>
              </div>

            </div>
          );
        })}
      </div>

      </CollapsibleContent>
    </Card>
  );
};
